/**
 * Lead Staging Area — mobile-first page showing the current sandbox contents.
 *
 * The voice assistant navigates here after starting a search.  Results appear
 * as the background job completes.  The user can remove items by tapping ✕ or
 * by voice, then tap "Import" (or say "import") to trigger the import flow.
 */
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  X,
  Loader2,
  SearchCheck,
  AlertCircle,
  Download,
  Star,
  StarOff,
} from "lucide-react";
import type { Region } from "@shared/schema";

interface SandboxItem {
  id: string;
  businessName: string;
  city: string | null;
  state: string | null;
  phone: string | null;
  website: string | null;
  isDuplicate: boolean;
  isAiGenerated?: boolean;
  aiFitScore?: number | null;
  googleRating?: number | null;
}

interface LeadGenSearch {
  id: string;
  status: "PENDING" | "RUNNING" | "COMPLETE" | "FAILED";
  keyword: string;
  location: string;
  completedSubSearches: number;
  totalSubSearches: number;
}

interface SandboxResponse {
  items: SandboxItem[];
  searches: LeadGenSearch[];
}

export default function LeadSandboxPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [importOpen, setImportOpen] = useState(false);
  const [clientType, setClientType] = useState("");
  const [regionId, setRegionId] = useState("");

  // Poll sandbox items + searches together — faster while a search is in flight.
  const {
    data: sandbox,
    isLoading: sandboxLoading,
  } = useQuery<SandboxResponse>({
    queryKey: ["/api/lead-gen/sandbox"],
    refetchInterval: 2_500,
    select: (raw: any) => ({
      items: (raw?.items ?? []) as SandboxItem[],
      searches: (raw?.searches ?? []) as LeadGenSearch[],
    }),
  });

  const items: SandboxItem[] = sandbox?.items ?? [];
  const activeSearch = (sandbox?.searches ?? []).find(
    (s) => s.status === "PENDING" || s.status === "RUNNING",
  );

  // Regions for import dialog.
  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
    select: (raw: any) => (Array.isArray(raw) ? raw : raw?.regions ?? []),
  });

  // Remove a single sandbox item.
  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/lead-gen/sandbox/${id}`, undefined);
      if (!res.ok) throw new Error("Failed to remove");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
    },
    onError: () => {
      toast({ title: "Failed to remove item", variant: "destructive" });
    },
  });

  // Import all remaining sandbox items.
  const importMutation = useMutation({
    mutationFn: async (body: { clientType: string; regionId: string }) => {
      const res = await apiRequest("POST", "/api/lead-gen/import", body);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message ?? "Import failed");
      }
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: `Imported ${data.imported} lead${data.imported === 1 ? "" : "s"}`,
        description:
          data.refunded > 0
            ? `${data.refunded} item(s) failed and were refunded.`
            : "Sandbox cleared.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      setImportOpen(false);
      navigate("/lead-gen");
    },
    onError: (err: any) => {
      toast({ title: "Import failed", description: err?.message, variant: "destructive" });
    },
  });

  function handleImport() {
    if (!clientType.trim()) {
      toast({ title: "Client type is required", variant: "destructive" });
      return;
    }
    if (!regionId) {
      toast({ title: "Region is required", variant: "destructive" });
      return;
    }
    importMutation.mutate({ clientType: clientType.trim(), regionId });
  }

  const isEmpty = items.length === 0 && !activeSearch && !sandboxLoading;

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/lead-gen")}
          aria-label="Back to Lead Gen"
          data-testid="button-back-lead-sandbox"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <div className="flex-1 min-w-0">
          <h1 className="font-semibold text-base leading-tight">Lead Staging</h1>
          {items.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {items.length} prospect{items.length === 1 ? "" : "s"} ready to review
            </p>
          )}
        </div>

        {items.length > 0 && (
          <Button
            size="sm"
            onClick={() => setImportOpen(true)}
            data-testid="button-open-import-dialog"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Import
          </Button>
        )}
      </div>

      {/* ── Active search progress ───────────────────────────────────────────── */}
      {activeSearch && (
        <div className="mx-4 mt-3 rounded-lg border bg-primary/5 border-primary/20 px-4 py-3 flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-primary truncate">
              Searching "{activeSearch.keyword}"
            </p>
            <p className="text-xs text-muted-foreground">
              {activeSearch.location}
              {activeSearch.totalSubSearches > 0
                ? ` · ${activeSearch.completedSubSearches}/${activeSearch.totalSubSearches} grids`
                : ""}
            </p>
          </div>
        </div>
      )}

      {/* ── List ──────────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto px-4 py-3 space-y-2">
        {sandboxLoading && items.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {isEmpty && (
          <div className="flex flex-col items-center justify-center py-20 text-center px-8">
            <SearchCheck className="w-10 h-10 text-muted-foreground/40 mb-3" />
            <p className="font-medium text-muted-foreground">Staging area is empty</p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Ask the assistant to search for prospects, then review them here.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/lead-gen")}
              data-testid="button-go-to-lead-gen"
            >
              Go to Lead Gen
            </Button>
          </div>
        )}

        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-start gap-3 rounded-xl border bg-card px-4 py-3 shadow-sm"
            data-testid={`card-sandbox-item-${item.id}`}
          >
            {/* ── Fit/rating indicator ─── */}
            <div className="mt-0.5 shrink-0">
              {item.isAiGenerated && item.aiFitScore != null ? (
                <span
                  className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                    item.aiFitScore <= 2
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      : item.aiFitScore === 3
                      ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                      : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                  }`}
                  title={`AI fit score: ${item.aiFitScore}`}
                >
                  {item.aiFitScore}
                </span>
              ) : item.googleRating != null ? (
                <span className="flex items-center gap-0.5 text-xs text-amber-500 font-medium">
                  <Star className="w-3 h-3 fill-current" />
                  {item.googleRating.toFixed(1)}
                </span>
              ) : (
                <StarOff className="w-4 h-4 text-muted-foreground/30 mt-0.5" />
              )}
            </div>

            {/* ── Content ─── */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm truncate" data-testid={`text-sandbox-name-${item.id}`}>
                  {item.businessName}
                </span>
                {item.isDuplicate && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                    Duplicate
                  </Badge>
                )}
                {item.isAiGenerated && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0 border-primary/30 text-primary">
                    AI
                  </Badge>
                )}
              </div>
              {(item.city || item.state) && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {[item.city, item.state].filter(Boolean).join(", ")}
                </p>
              )}
              {item.phone && (
                <p className="text-xs text-muted-foreground">{item.phone}</p>
              )}
            </div>

            {/* ── Remove button ─── */}
            <button
              type="button"
              onClick={() => removeMutation.mutate(item.id)}
              disabled={removeMutation.isPending}
              aria-label={`Remove ${item.businessName}`}
              data-testid={`button-remove-sandbox-${item.id}`}
              className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {/* ── Sticky import footer ─────────────────────────────────────────────── */}
      {items.length > 0 && (
        <div className="sticky bottom-0 border-t bg-background/95 backdrop-blur px-4 py-3 pb-safe-or-3 md:pb-3" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))' }}>
          <Button
            className="w-full"
            size="lg"
            onClick={() => setImportOpen(true)}
            data-testid="button-import-leads-footer"
          >
            <Download className="w-4 h-4 mr-2" />
            Import {items.length} Lead{items.length === 1 ? "" : "s"}
          </Button>
        </div>
      )}

      {/* ── Import dialog ────────────────────────────────────────────────────── */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Import {items.length} Lead{items.length === 1 ? "" : "s"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="import-client-type">Client Type</Label>
              <Input
                id="import-client-type"
                placeholder="e.g. fair, casino, performing arts center"
                value={clientType}
                onChange={(e) => setClientType(e.target.value)}
                data-testid="input-import-client-type"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="import-region">Region</Label>
              {regions.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground rounded-lg border px-3 py-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  No regions configured. Ask your admin to add regions.
                </div>
              ) : (
                <Select value={regionId} onValueChange={setRegionId}>
                  <SelectTrigger id="import-region" data-testid="select-import-region">
                    <SelectValue placeholder="Select a region…" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((r) => (
                      <SelectItem key={r.id} value={r.id} data-testid={`option-region-${r.id}`}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setImportOpen(false)}
              data-testid="button-cancel-import"
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={importMutation.isPending || !clientType.trim() || !regionId}
              data-testid="button-confirm-import"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing…
                </>
              ) : (
                "Import"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
